import { DISPOSITION_PAGINATION_PAYLOAD } from 'app/modules/dispositions/constants';

import { formatDisposition } from 'app/shared/utils/disposition';
import { useGetDispositions } from 'app/modules/dispositions/queries/useGetDispositions';
import { useMemo } from 'react';

import {
  U21MultiSelect,
  U21MultiSelectProps,
  U21Select,
  U21SelectProps,
} from 'app/shared/u21-ui/components';

interface U21DispositionSelectSingleProps<
  TClearable extends boolean,
> extends Omit<U21SelectProps<string, TClearable>, 'options'> {
  multi?: false;
  useTitle?: boolean;
}

interface U21DispositionSelectMultiProps<
  TClearable extends boolean,
> extends Omit<U21MultiSelectProps<string, TClearable>, 'options'> {
  multi: true;
  useTitle?: boolean;
}

export type U21DispositionSelectProps<TClearable extends boolean> =
  | U21DispositionSelectSingleProps<TClearable>
  | U21DispositionSelectMultiProps<TClearable>;

export const U21DispositionSelect = <TClearable extends boolean>(
  props: U21DispositionSelectProps<TClearable>,
) => {
  const { multi, useTitle, onChange, value, ...rest } = props;

  const { data: { dispositions = [] } = {} } = useGetDispositions(
    DISPOSITION_PAGINATION_PAYLOAD,
  );

  const options = useMemo(() => {
    const result = [
      ...dispositions
        .reduce<typeof dispositions>((acc, i) => {
          if (!acc.find((a) => a.title === i.title)) {
            acc.push(i);
          }
          return acc;
        }, [])
        .map((i) => ({
          text: formatDisposition(i.title),
          value: useTitle ? i.title : i.id,
        })),
    ];

    if (!result.find((i) => i.text === 'Unresolved')) {
      result.push({
        text: 'Unresolved',
        value: 'UNRESOLVED',
      });
    }

    return result;
  }, [dispositions, useTitle]);

  if (multi) {
    return (
      <U21MultiSelect
        onChange={onChange}
        options={options}
        value={value}
        {...rest}
      />
    );
  }
  return (
    <U21Select
      allowInvalidValues
      onChange={onChange}
      options={options}
      value={value}
      {...rest}
    />
  );
};
